/**
 * ELF-CIRCLE events — section `#7b`, "The season, on the wall".
 *
 * Poster-led grid: category chips filter it, hovering a poster lifts capacity
 * and price onto the artwork, the heart saves it, and clicking anywhere else
 * opens a quick-look over the grid with ticket tiers and a running total —
 * so nobody loses their place in the season.
 */

import React, { useMemo, useState } from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import type { LandingSlot } from './landingImages';
import { useLandingImages } from './useLandingImages';
import { useConsult } from './elfConsult';

type Category = 'Music' | 'Fashion' | 'Film' | 'Street';

interface Tier {
  name: string;
  price: number;
  available: boolean;
}

interface ElfEvent {
  /** Stable identity — React keys and content keys hang off this, never the
      title, which an admin can change. */
  slug: string;
  title: string;
  category: Category;
  slot: LandingSlot;
  flag: string;
  when: string;
  /** Poster line-up — the shorter of the two billings. */
  line: string;
  /** Quick-look line-up, which runs longer than the poster's. */
  fullLine: string;
  /** Display string, e.g. `from $48` — editable copy, not a computed value. */
  price: string;
  /** Capacity sold, 0–100. */
  full: number;
  tiers: Tier[];
}

const EVENTS: ElfEvent[] = [
  {
    slug: 'neon-circuit',
    title: 'Neon Circuit',
    category: 'Music',
    slot: 'event.neon-circuit',
    flag: 'Selling fast',
    when: '12 Sep 2026 · Berlin',
    line: 'Kito Vale · SANS · Deep Marrow',
    fullLine: 'Kito Vale · SANS · Deep Marrow · Ottoline',
    price: 'from $48',
    full: 82,
    tiers: [
      { name: 'General admission', price: 48, available: true },
      { name: 'Early entry', price: 72, available: true },
      { name: 'Backstage pass', price: 180, available: false },
    ],
  },
  {
    slug: 'cut-thread',
    title: 'Cut & Thread',
    category: 'Fashion',
    slot: 'event.cut-thread',
    flag: 'Open',
    when: '04 Oct 2026 · Milan',
    line: 'Marisol Reyes · ATELIER 9 · Nam Ito',
    fullLine: 'Marisol Reyes · ATELIER 9 · Nam Ito',
    price: 'from $65',
    full: 46,
    tiers: [
      { name: 'Standard seat', price: 65, available: true },
      { name: 'Front row', price: 140, available: true },
      { name: 'Show + afterparty', price: 210, available: true },
    ],
  },
  {
    slug: 'frame-rate',
    title: 'Frame Rate',
    category: 'Film',
    slot: 'event.frame-rate',
    flag: 'Open',
    when: '22 Oct 2026 · Lisbon',
    line: 'Opening: SALT · Shorts block · Dir. Q&A',
    fullLine: 'Opening: SALT · Shorts block · Dir. Q&A',
    price: 'from $32',
    full: 64,
    tiers: [
      { name: 'Single screening', price: 32, available: true },
      { name: 'Day pass', price: 85, available: true },
      { name: 'Festival pass', price: 190, available: true },
    ],
  },
  {
    slug: 'block-party',
    title: 'Block Party',
    category: 'Street',
    slot: 'event.block-party',
    flag: 'Early access',
    when: '15 Nov 2026 · London',
    line: 'Jaxon B · Third Rail · CREW04',
    fullLine: 'Jaxon B · Third Rail · CREW04',
    price: 'from $28',
    full: 24,
    tiers: [
      { name: 'Entry', price: 28, available: true },
      { name: 'Entry + workshop', price: 60, available: true },
      { name: 'Crew pass', price: 110, available: true },
    ],
  },
];

const FILTERS: ('All' | Category)[] = ['All', 'Music', 'Fashion', 'Film', 'Street'];
const CITIES = '5';
/** Per-tier cap in the quantity stepper. */
const MAX_QTY = 8;

export const ElfEvents: React.FC = () => {
  const { src, text } = useLandingImages();
  const consult = useConsult();
  const [filter, setFilter] = useState<'All' | Category>('All');
  const [saved, setSaved] = useState<Set<number>>(() => new Set());
  const [quickLook, setQuickLook] = useState<number | null>(null);
  /** Tier index → quantity, reset whenever a different event is opened. */
  const [qty, setQty] = useState<Record<number, number>>({});

  const visible = useMemo(
    () => EVENTS.filter((e) => filter === 'All' || e.category === filter),
    [filter]
  );

  /** Copy for a given event field, honouring any admin override. */
  const ev = (e: ElfEvent, field: string, fallback: string) =>
    text(`event.${e.slug}.${field}`, fallback);

  const openQuickLook = (index: number) => {
    setQty({});
    setQuickLook(index);
  };

  const toggleSaved = (index: number) => {
    setSaved((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const step = (tier: number, delta: number) =>
    setQty((prev) => ({
      ...prev,
      [tier]: Math.max(0, Math.min(MAX_QTY, (prev[tier] ?? 0) + delta)),
    }));

  const event = quickLook === null ? null : EVENTS[quickLook];

  const { tickets, total } = useMemo(() => {
    if (!event) return { tickets: 0, total: 0 };
    return event.tiers.reduce(
      (acc, tier, i) => {
        const n = qty[i] ?? 0;
        return { tickets: acc.tickets + n, total: acc.total + n * tier.price };
      },
      { tickets: 0, total: 0 }
    );
  }, [event, qty]);

  return (
    <section id="events" className="elf-band elf-events">
      <EditableImage slot="events.backdrop" className="elf-events__bg" alt="" />
      <div className="elf-events__scrim" />
      <div className="sigrule elf-band__rule" />
      <div className="elf-band__inner">
        <div className="elf-section__head" style={{ marginBottom: 30 }}>
          <div style={{ maxWidth: 600 }}>
            <span className="eyebrow an" style={{ animationDelay: '.05s' }}>
              <i />
              <EditableText contentKey="events.eyebrow" fallback="Events & festivals" label="Eyebrow" />
            </span>
            <EditableText
              as="h2"
              contentKey="events.title"
              fallback="The season, on the wall"
              label="Section title"
              className="an elf-section__title"
              style={{ animationDelay: '.12s' }}
            />
            <EditableText
              as="p"
              contentKey="events.lede"
              fallback="Events lead with the artwork because that is what gets shared. Hover lifts capacity and price onto the poster; quick look opens the ticket tiers over the grid so nobody loses their place."
              label="Section intro"
              multiline
              className="an elf-section__lede"
              style={{ animationDelay: '.18s' }}
            />
          </div>
          <div className="an elf-events__filters" style={{ animationDelay: '.24s' }}>
            <div className="elf-events__chips">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={f === filter ? 'fchip on' : 'fchip'}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="live">
              <i />
              <span>
                {visible.length} {visible.length === 1 ? 'event' : 'events'}
              </span>{' '}
              · <EditableText contentKey="events.cities" fallback={CITIES} label="City count" /> cities
            </div>
          </div>
        </div>

        <div className="elf-events__grid">
          {EVENTS.map((e, i) => {
            const hidden = filter !== 'All' && e.category !== filter;
            return (
              <div
                key={e.slug}
                className={hidden ? 'poster hid' : 'poster'}
                role="button"
                tabIndex={hidden ? -1 : 0}
                onClick={() => openQuickLook(i)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    openQuickLook(i);
                  }
                }}
              >
                <EditableImage slot={e.slot} alt="" />
                <button
                  type="button"
                  className={saved.has(i) ? 'heart on' : 'heart'}
                  aria-label={saved.has(i) ? `Unsave ${ev(e, 'title', e.title)}` : `Save ${ev(e, 'title', e.title)}`}
                  aria-pressed={saved.has(i)}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    toggleSaved(i);
                  }}
                >
                  ♥
                </button>
                <EditableText
                  as="div"
                  contentKey={`event.${e.slug}.flag`}
                  fallback={e.flag}
                  label="Badge"
                  className="elf-poster__flag"
                />
                <div className="pmeta">
                  <EditableText
                    as="div"
                    contentKey={`event.${e.slug}.when`}
                    fallback={e.when}
                    label="Date & city"
                    className="elf-poster__when"
                  />
                  <EditableText
                    as="div"
                    contentKey={`event.${e.slug}.title`}
                    fallback={e.title}
                    label="Event title"
                    className="elf-poster__title"
                  />
                  <EditableText
                    as="div"
                    contentKey={`event.${e.slug}.line`}
                    fallback={e.line}
                    label="Line-up"
                    className="elf-poster__line"
                  />
                  <div className="plift">
                    <div className="elf-poster__fill">
                      <div className="meter">
                        <i style={{ width: `${e.full}%` }} />
                      </div>
                      <span>{e.full}% full</span>
                    </div>
                    <div className="elf-poster__buy">
                      <EditableText
                        contentKey={`event.${e.slug}.price`}
                        fallback={e.price}
                        label="Price line"
                        className="elf-poster__from"
                      />
                      <span className="pillcta">Quick look</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="elf-events__foot">
          <div>
            <b>{saved.size}</b> saved ·{' '}
            <EditableText
              contentKey="events.note"
              fallback="four more dates land before the year is out."
              label="Footer note"
            />
          </div>
          <button
            className="pillghost"
            type="button"
            onClick={() =>
              consult?.open({
                source: 'events.calendar',
                interest: 'Events & Festivals',
                title: 'Send me the full calendar',
              })
            }
          >
            <EditableText contentKey="events.cta.calendar" fallback="Full calendar" label="Button label" />{' '}
            <span>→</span>
          </button>
          <button
            className="pillcta mag"
            type="button"
            onClick={() =>
              consult?.open({
                source: 'events.join',
                interest: 'Events & Festivals',
                title: 'Join the list',
              })
            }
          >
            <EditableText contentKey="events.cta.join" fallback="Join the list" label="Button label" />{' '}
            <span>→</span>
          </button>
        </div>
      </div>

      {event && (
        <div
          className="qlwrap on"
          role="dialog"
          aria-modal="true"
          aria-label={`${ev(event, 'title', event.title)} tickets`}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setQuickLook(null);
          }}
        >
          <div className="qlcard">
            <div className="elf-ql__art">
              <img src={src(event.slot)} alt="" />
              <div className="elf-ql__art-scrim" />
              <div className="elf-ql__art-body">
                <div className="elf-ql__date">{ev(event, 'when', event.when)}</div>
                <div className="elf-ql__title">{ev(event, 'title', event.title)}</div>
              </div>
            </div>
            <div className="elf-ql__body">
              <div className="elf-ql__head">
                <div className="mono">Tickets</div>
                <button
                  className="chipx"
                  type="button"
                  style={{ borderRadius: 10 }}
                  onClick={() => setQuickLook(null)}
                >
                  Close ✕
                </button>
              </div>

              <div className="elf-ql__tiers">
                {event.tiers.map((tier, i) => {
                  const n = qty[i] ?? 0;
                  const classes = ['tierrow'];
                  if (!tier.available) classes.push('gone');
                  if (n > 0) classes.push('picked');
                  return (
                    <div key={tier.name} className={classes.join(' ')}>
                      <div>
                        <div className="elf-tier__name">{tier.name}</div>
                        <div className="elf-tier__note">
                          {tier.available ? 'Includes entry' : 'Sold out'}
                        </div>
                      </div>
                      <div className="elf-tier__price">${tier.price}</div>
                      {tier.available ? (
                        <div className="qty">
                          <button
                            type="button"
                            disabled={n === 0}
                            aria-label={`Remove one ${tier.name}`}
                            onClick={() => step(i, -1)}
                          >
                            −
                          </button>
                          <b>{n}</b>
                          <button
                            type="button"
                            disabled={n === MAX_QTY}
                            aria-label={`Add one ${tier.name}`}
                            onClick={() => step(i, 1)}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          className="chipx"
                          type="button"
                          style={{ borderRadius: 10 }}
                          onClick={() =>
                            consult?.open({
                              source: `events.waitlist.${event.slug}`,
                              interest: 'Events & Festivals',
                              title: `Waitlist — ${tier.name}`,
                            })
                          }
                        >
                          Waitlist
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="elf-ql__bill">
                <span className="mono" style={{ marginRight: 2 }}>
                  Bill
                </span>
                <EditableText
                  contentKey={`event.${event.slug}.fullLine`}
                  fallback={event.fullLine}
                  label="Full line-up"
                />
              </div>

              <div className="elf-ql__foot">
                <div style={{ flex: 1 }}>
                  <div className="mono">
                    Total{tickets > 0 ? ` · ${tickets} ${tickets === 1 ? 'ticket' : 'tickets'}` : ''}
                  </div>
                  <div className="elf-ql__total">${total}</div>
                </div>
                <button
                  className="pillcta mag"
                  type="button"
                  onClick={() =>
                    consult?.open({
                      source: `events.register.${event.slug}`,
                      interest: 'Events & Festivals',
                      title: `Register — ${ev(event, 'title', event.title)}`,
                    })
                  }
                >
                  Register <span>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ElfEvents;
